import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { API_URLS } from '../../../config/api';
import { Plus, Edit, Trash2, Save, X, HelpCircle, MessageSquare } from 'lucide-react';

const categoryColorOptions = [
  { value: 'from-blue-500 to-cyan-500', label: 'Mavi' },
  { value: 'from-purple-500 to-pink-500', label: 'Mor' },
  { value: 'from-green-500 to-emerald-500', label: 'Yeşil' },
  { value: 'from-orange-500 to-red-500', label: 'Turuncu' },
  { value: 'from-indigo-500 to-purple-500', label: 'İndigo' },
  { value: 'from-pink-500 to-rose-500', label: 'Pembe' },
  { value: 'from-gray-600 to-gray-800', label: 'Gri' },
  { value: 'from-teal-500 to-cyan-500', label: 'Turkuaz' },
  { value: 'from-yellow-500 to-orange-500', label: 'Sarı' },
  { value: 'from-red-500 to-pink-500', label: 'Kırmızı' }
];

const AdminFAQ = () => {
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Form states
  const [categoryForm, setCategoryForm] = useState({
    title: '',
    description: '',
    color: 'from-blue-500 to-cyan-500',
    sort_order: 0
  });

  const [questionForm, setQuestionForm] = useState({
    question: '',
    answer: '',
    category_id: '',
    sort_order: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URLS.ADMIN.FAQ);

      if (response.ok) {
        const data = await response.json();
        setCategories(data.categories || []);
        setQuestions(data.questions || []);
      }
    } catch (error) {
      console.error('Error fetching FAQ data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingCategory 
        ? API_URLS.ADMIN.UPDATE_FAQ_CATEGORY
        : API_URLS.ADMIN.CREATE_FAQ_CATEGORY;
      
      const method = editingCategory ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...categoryForm,
          id: editingCategory?.id
        })
      });

      if (response.ok) {
        await fetchData();
        resetCategoryForm();
        setShowCategoryModal(false);
      }
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const handleQuestionSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingQuestion 
        ? API_URLS.ADMIN.UPDATE_FAQ_QUESTION
        : API_URLS.ADMIN.CREATE_FAQ_QUESTION;
      
      const method = editingQuestion ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...questionForm,
          id: editingQuestion?.id
        })
      });

      if (response.ok) {
        await fetchData();
        resetQuestionForm();
        setShowQuestionModal(false);
      }
    } catch (error) {
      console.error('Error saving question:', error);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz? (Kategoriye ait sorular da silinecek)')) {
      try {
        const response = await fetch(`${API_URLS.ADMIN.DELETE_FAQ_CATEGORY}&id=${encodeURIComponent(categoryId)}`);
        if (response.ok) {
          await fetchData();
        }
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  const handleDeleteQuestion = async (questionId) => {
    if (window.confirm('Bu soruyu silmek istediğinizden emin misiniz?')) {
      try {
        const response = await fetch(`${API_URLS.ADMIN.DELETE_FAQ_QUESTION}&id=${encodeURIComponent(questionId)}`);
        if (response.ok) {
          await fetchData();
        }
      } catch (error) {
        console.error('Error deleting question:', error);
      }
    }
  };

  const resetCategoryForm = () => {
    setCategoryForm({ title: '', description: '', color: 'from-blue-500 to-cyan-500', sort_order: 0 });
    setEditingCategory(null);
  };

  const resetQuestionForm = () => {
    setQuestionForm({ question: '', answer: '', category_id: '', sort_order: 0 });
    setEditingQuestion(null);
  };

  const openCategoryModal = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setCategoryForm({
        title: category.title,
        description: category.description,
        color: category.color || 'from-blue-500 to-cyan-500',
        sort_order: category.sort_order
      });
    } else {
      resetCategoryForm();
    }
    setShowCategoryModal(true);
  };

  const openQuestionModal = (question = null, category = null) => {
    if (question) {
      setEditingQuestion(question);
      setQuestionForm({
        question: question.question,
        answer: question.answer,
        category_id: question.category_id,
        sort_order: question.sort_order
      });
    } else {
      resetQuestionForm();
      // Eğer kategori parametresi verilmişse onu kullan, yoksa selectedCategory'yi kullan
      const targetCategory = category || selectedCategory;
      if (targetCategory) {
        setQuestionForm(prev => ({ ...prev, category_id: targetCategory.id }));
      }
    }
    setShowQuestionModal(true);
  };

  const getQuestionsForCategory = (categoryId) => {
    return questions.filter(q => q.category_id === categoryId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">SSS Yönetimi</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Sık sorulan soruları ve kategorilerini yönetin</p>
        </div>
        <div className="flex flex-col sm:flex-row w-full sm:w-auto gap-2 sm:gap-3">
          <Button onClick={() => openCategoryModal()} className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Kategori Ekle
          </Button>
          <Button onClick={() => openQuestionModal()} className="bg-green-600 hover:bg-green-700 w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Soru Ekle
          </Button>
        </div>
      </div>

      {/* Categories */}
      <div className="grid gap-6">
        {categories.map((category) => {
          const categoryQuestions = getQuestionsForCategory(category.id);
          return (
            <Card key={category.id} className="overflow-hidden">
              <CardHeader className="bg-gray-50">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2">
                      <div className={`w-5 h-5 rounded bg-gradient-to-r ${category.color || 'from-blue-500 to-cyan-500'}`}></div>
                      {category.title}
                    </CardTitle>
                    <p className="text-sm text-gray-600 mt-1">{category.description}</p>
                    <Badge variant="secondary" className="mt-2">
                      {categoryQuestions.length} soru
                    </Badge>
                  </div>
                  <div className="flex gap-2 w-full sm:w-auto">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openQuestionModal(null, category)}
                      className="flex-1 sm:flex-none"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Soru Ekle</span>
                      <span className="sm:hidden">Ekle</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openCategoryModal(category)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteCategory(category.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {categoryQuestions.length > 0 ? (
                  <div className="divide-y">
                    {categoryQuestions.map((question) => (
                      <div key={question.id} className="p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-medium text-gray-900 mb-2">
                              {question.question}
                            </h4>
                            <p className="text-gray-600 text-sm">
                              {question.answer}
                            </p>
                          </div>
                          <div className="flex space-x-2 ml-4">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openQuestionModal(question)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteQuestion(question.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Bu kategoride henüz soru bulunmuyor</p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-3"
                      onClick={() => openQuestionModal(null, category)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      İlk Soruyu Ekle
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingCategory ? 'Kategori Düzenle' : 'Yeni Kategori'}
            </h3>
            <form onSubmit={handleCategorySubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kategori Adı
                </label>
                <Input
                  value={categoryForm.title}
                  onChange={(e) => setCategoryForm({ ...categoryForm, title: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Açıklama
                </label>
                <Textarea
                  value={categoryForm.description}
                  onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Renk
                </label>
                <div className="flex flex-wrap gap-2">
                  {categoryColorOptions.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setCategoryForm({ ...categoryForm, color: color.value })}
                      className={`w-8 h-8 rounded-lg border-2 transition-all ${
                        categoryForm.color === color.value
                          ? 'border-gray-800 scale-110'
                          : 'border-gray-300 hover:border-gray-400'
                      } bg-gradient-to-r ${color.value}`}
                      title={color.label}
                    />
                  ))}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Seçili: {categoryColorOptions.find(c => c.value === categoryForm.color)?.label || 'Mavi'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sıralama
                </label>
                <Input
                  type="number"
                  value={categoryForm.sort_order}
                  onChange={(e) => setCategoryForm({ ...categoryForm, sort_order: parseInt(e.target.value) })}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={() => setShowCategoryModal(false)}>
                  <X className="w-4 h-4 mr-2" />
                  İptal
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Question Modal */}
      {showQuestionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-4">
              {editingQuestion ? 'Soru Düzenle' : 'Yeni Soru'}
            </h3>
            <form onSubmit={handleQuestionSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kategori
                </label>
                <select
                  value={questionForm.category_id}
                  onChange={(e) => setQuestionForm({ ...questionForm, category_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Kategori Seçin</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Soru
                </label>
                <Input
                  value={questionForm.question}
                  onChange={(e) => setQuestionForm({ ...questionForm, question: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cevap
                </label>
                <Textarea
                  value={questionForm.answer}
                  onChange={(e) => setQuestionForm({ ...questionForm, answer: e.target.value })}
                  rows={4}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sıralama
                </label>
                <Input
                  type="number"
                  value={questionForm.sort_order}
                  onChange={(e) => setQuestionForm({ ...questionForm, sort_order: parseInt(e.target.value) })}
                />
              </div>
              <div className="flex justify-end space-x-3">
                <Button type="button" variant="outline" onClick={() => setShowQuestionModal(false)}>
                  <X className="w-4 h-4 mr-2" />
                  İptal
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  <Save className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFAQ;
